import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigation } from 'context/NavigationContext';
import { BringzzPageContainer, BringzzInput, BringzzIcon, BringzzText } from "@bringzz/components";
import HeaderCategory from './HeaderCategory';
import SearchFooter from './SearchFooter';
import SearchModal from './SearchModal';
import { getCategories, alphabets } from './constants';
import classNames from 'classnames';
import useRequest from 'hooks/useRequest';
import { useTranslation } from 'react-i18next';
import { getPharmacyStatus } from 'utils/pharmacyUtils';

const INITIAL_HEADER_HEIGHT = 214;

const LocalPharmaciesPage = () => {
    const { t } = useTranslation();
    const { navigate } = useNavigation();
    const { sendRequest, loading } = useRequest();
    const [headerHeight, setHeaderHeight] = useState(INITIAL_HEADER_HEIGHT);
    const [MIN_HEADER_HEIGHT, SET_MIN_HEADER_HEIGHT] = useState(100);
    const [alphabetFilter, setAlphabetFilter] = useState('All');
    const [pharmacies, setPharmacies] = useState([]);
    const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);
    const [searchData, setSearchData] = useState({
        method: "category",
        value: t('component.search.categories.local_pharmacies.short') || 'Local Pharmacies',
        results: []
    });

    const allCategories = getCategories(t);

    useEffect(() => {
        const fetchPharmacies = async () => {
            try {
                const response = await sendRequest({
                    method: 'GET',
                    url: '/module/pharmacy/getLocalPharmacies'
                });

                if (response.status === 'success' && response.data) {
                    const formattedPharmacies = response.data.map(pharmacy => {
                        const nightDutyInterval = pharmacy.night_duty_interval || 5;
                        const status = getPharmacyStatus(pharmacy.opening_hours, pharmacy.next_night_duty, nightDutyInterval, t);

                        return {
                            label: pharmacy.name,
                            id: pharmacy.id,
                            integrationId: pharmacy.integrationId,
                            pharmacyPic: pharmacy.pharmacyPic || null,
                            address: pharmacy.address,
                            city: pharmacy.address?.city_name || null,
                            distance: pharmacy.distance || null,
                            isOpen: status.isOpen,
                            statusText: status.statusText,
                            statusTimeText: status.statusTimeText,
                            statusColor: status.isOpen ? 'text-green-600' : 'text-red-600'
                        };
                    });
                    setPharmacies(formattedPharmacies);
                    setSearchData(prev => ({ ...prev, results: formattedPharmacies }));
                }
            } catch (error) {
                console.error('Error fetching local pharmacies:', error);
            }
        };

        fetchPharmacies();
    }, [sendRequest, t]);

    const handlePharmacyClick = (pharmacy) => {
        if (pharmacy.integrationId) {
            navigate(`/pharmacy/profile/${pharmacy.integrationId}`);
        }
    };

    const filterPharmaciesByAlphabet = (pharmacies, filter) => {
        if (!pharmacies) return [];
        return pharmacies.filter((pharmacy) => {
            if (filter === 'All' || filter === '#') {
                return true;
            } else {
                return pharmacy.label.toUpperCase().startsWith(filter);
            }
        });
    };

    const filteredPharmacies = filterPharmaciesByAlphabet(pharmacies, alphabetFilter);

    return (
        <BringzzPageContainer
            className="bg-sand"
            footerHover={true}
            footer={
                <div className='p-4'>
                    <BringzzInput
                        className='shadow-xl border-slate-400 bg-gray-100 cursor-pointer'
                        placeholder={t('component.search.general.find_items')}
                        readOnly
                        onClick={() => setIsSearchModalOpen(true)}
                    />
                </div>
            }
        >
            <div className='relative'>
                <HeaderCategory
                    SET_MIN_HEADER_HEIGHT={SET_MIN_HEADER_HEIGHT}
                    searchData={searchData}
                    setSearchData={setSearchData}
                    headerHeight={headerHeight}
                    categories={allCategories}
                />
                <div style={{ paddingTop: `${INITIAL_HEADER_HEIGHT}px` }}>
                    {loading ? (
                        <div className="flex items-center justify-center p-8">
                            <BringzzIcon
                                size="32"
                                icon="IconSpinner"
                                folder="LineIcons"
                                className="animate-spin text-magic-lilac"
                            />
                        </div>
                    ) : (
                        <>
                            <div className='rounded-xl bg-white mx-2 py-2'>
                                <div className='flex items-center overflow-x-auto sticky top-0 bg-white z-10 py-2' style={{ paddingLeft: '12px', paddingRight: '12px' }}>
                                    <BringzzText tag='h3' className='whitespace-nowrap mr-1'>{t('component.search.general.alphabet_filter')}</BringzzText>
                                    <div className='flex space-x-3 sm:space-x-2 min-w-0'>
                                        {alphabets.map((char, index) => {
                                            const isSelected = char === alphabetFilter;
                                            return (
                                                <div
                                                    key={index}
                                                    className={classNames('bg-sand p-2 rounded-full cursor-pointer flex-shrink-0',
                                                        isSelected && "!bg-magic-lilac"
                                                    )}
                                                    onClick={() => setAlphabetFilter(char)}
                                                >
                                                    <BringzzText tag='h5'>{char}</BringzzText>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                                <div>
                                    {filteredPharmacies.map((pharmacy, index) => (
                                        <div
                                            key={pharmacy.id}
                                            className={classNames(
                                                'h-16 cursor-pointer flex justify-between px-6 items-center',
                                                index !== filteredPharmacies.length - 1 && 'border-b',
                                                'hover:bg-magic-lilac/10 transition-colors duration-300'
                                            )}
                                            onClick={() => handlePharmacyClick(pharmacy)}
                                        >
                                            <div className='flex items-center space-x-3'>
                                                {pharmacy.pharmacyPic ? (
                                                    <img
                                                        src={pharmacy.pharmacyPic}
                                                        alt={pharmacy.label}
                                                        className='w-10 h-10 rounded-full object-cover flex-shrink-0'
                                                        onError={(e) => {
                                                            e.target.style.display = 'none';
                                                            e.target.nextSibling.style.display = 'flex';
                                                        }}
                                                    />
                                                ) : null}
                                                <div
                                                    className={`w-10 h-10 rounded-full bg-magic-lilac flex items-center justify-center flex-shrink-0 ${pharmacy.pharmacyPic ? 'hidden' : 'flex'}`}
                                                >
                                                    <BringzzText tag='h5' className='text-white font-bold'>
                                                        {pharmacy.label.charAt(0).toUpperCase()}
                                                    </BringzzText>
                                                </div>
                                                <div className='flex flex-col'>
                                                    <BringzzText tag='h4'>{pharmacy.label}</BringzzText>
                                                    <div className='flex items-center space-x-2'>
                                                        {pharmacy.distance && (
                                                            <BringzzText tag='p' className='text-xs text-gray-500'>
                                                                {pharmacy.distance}km
                                                            </BringzzText>
                                                        )}
                                                        {pharmacy.distance && pharmacy.city && (
                                                            <BringzzText tag='p' className='text-xs text-gray-400'>•</BringzzText>
                                                        )}
                                                        {pharmacy.city && (
                                                            <BringzzText tag='p' className='text-xs text-gray-500'>
                                                                {pharmacy.city}
                                                            </BringzzText>
                                                        )}
                                                    </div>
                                                    <div className='flex items-center space-x-1'>
                                                        <BringzzText tag='p' className={`text-xs font-medium ${pharmacy.statusColor}`}>
                                                            {pharmacy.statusText}
                                                        </BringzzText>
                                                        {pharmacy.statusTimeText && (
                                                            <>
                                                                <BringzzText tag='p' className='text-xs text-gray-400'>•</BringzzText>
                                                                <BringzzText tag='p' className='text-xs text-gray-500'>
                                                                    {pharmacy.statusTimeText}
                                                                </BringzzText>
                                                            </>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                            <BringzzIcon
                                                size="16"
                                                icon="IconInfoCircle"
                                                folder="LineIcons"
                                                className="text-magic-lilac"
                                            />
                                        </div>
                                    ))}
                                    {filteredPharmacies.length === 0 && (
                                        <div className='px-6 py-4'>
                                            <BringzzText tag='p'>{t('component.search.pharmacy.no_pharmacies_found')}</BringzzText>
                                        </div>
                                    )}
                                </div>
                            </div>
                            <SearchFooter />
                        </>
                    )}
                </div>
            </div>
            <SearchModal 
                isOpen={isSearchModalOpen} 
                onClose={() => setIsSearchModalOpen(false)}
            />
        </BringzzPageContainer>
    );
};

export default LocalPharmaciesPage;